<template>
  <div class="int-root">

    <!-- Total Invoiced tracks the Invoice view's own current filter/search
         (see InvoiceViewTable.vue's summary-changed emit) — the rest stay
         static for now, per product decision, until payment tracking exists. -->
    <div v-if="!hideSummaryCards" class="int-summary-cards">
      <div class="int-card">
        <div class="int-card__label">Total Invoiced</div>
        <div class="int-card__value">{{ totalInvoicedAmount !== null ? formatPlainNumber(totalInvoicedAmount) : '—' }}</div>
        <div class="int-card__sub">{{ totalInvoicedCount !== null ? totalInvoicedCount : '—' }} invoice{{ totalInvoicedCount === 1 ? '' : 's' }}</div>
      </div>
      <div class="int-card">
        <div class="int-card__label">Paid</div>
        <div class="int-card__value int-card__value--paid">—</div>
        <div class="int-card__sub">—</div>
      </div>
      <div class="int-card">
        <div class="int-card__label">Outstanding</div>
        <div class="int-card__value int-card__value--outstanding">—</div>
        <div class="int-card__sub">—</div>
      </div>
      <div class="int-card">
        <div class="int-card__label">Overdue</div>
        <div class="int-card__value">—</div>
        <div class="int-card__sub">All on time</div>
      </div>
    </div>

    <!-- View toggle + export -->
    <div class="int-toolbar">
      <div class="int-toolbar__label">{{ activeView === 'invoice' ? 'Invoice view' : 'Itemized view' }}</div>
      <div class="int-toolbar__actions">
        <b-button size="sm" variant="outline-secondary" :disabled="exporting" @click="handleExport">
          <b-spinner v-if="exporting" small class="mr-1" />
          ↓ Export {{ activeView === 'invoice' ? 'Invoices' : 'Charges' }}
        </b-button>
        <div class="int-view-toggle">
          <button
            type="button"
            class="int-view-toggle__btn"
            :class="{ 'int-view-toggle__btn--active': activeView === 'invoice' }"
            @click="activeView = 'invoice'"
          >
            Invoice view
          </button>
          <button
            type="button"
            class="int-view-toggle__btn"
            :class="{ 'int-view-toggle__btn--active': activeView === 'itemized' }"
            @click="activeView = 'itemized'"
          >
            Itemized view
          </button>
        </div>
      </div>
    </div>

    <!-- Each view fetches its own page of data server-side (pagination/
         filter/sort) — see InvoiceViewTable.vue / ItemizedInvoicesTable.vue. -->
    <InvoiceViewTable
      v-if="activeView === 'invoice'"
      ref="invoiceTableRef"
      @summary-changed="handleSummaryChanged"
    />
    <ItemizedInvoicesTable v-else ref="itemizedTableRef" />

  </div>
</template>

<script>
import { defineComponent, ref, computed, getCurrentInstance } from '@vue/composition-api'

import InvoiceViewTable from './InvoiceViewTable.vue'
import ItemizedInvoicesTable from './ItemizedInvoicesTable.vue'

// No currency symbol — the sum mixes invoices across different
// currency_types, so a single $/£/€ prefix (formatCurrency's usual job)
// would misrepresent the total. Plain thousands-separated number instead.
const formatPlainNumber = (val) => {
  if (val === null || val === undefined) return '—'
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val)
}

export default defineComponent({
  name: 'InvoicingTab',

  components: {
    InvoiceViewTable,
    ItemizedInvoicesTable,
  },

  props: {
    hideSummaryCards: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const proxy = getCurrentInstance().proxy

    const activeView = ref('invoice')

    // null until InvoiceViewTable's first fetch resolves (shows '—' in
    // the meantime) — tracks whatever's currently filtered/searched
    // there, not the grand total across every invoice ever dispatched.
    // Stays at its last value while the Itemized view is active, since
    // InvoiceViewTable unmounts (v-if) and stops emitting then.
    const totalInvoicedAmount = ref(null)
    const totalInvoicedCount = ref(null)

    const handleSummaryChanged = ({ totalAmount, count }) => {
      totalInvoicedAmount.value = totalAmount
      totalInvoicedCount.value = count
    }

    // Only one of these is ever mounted at a time (v-if/v-else on
    // activeView) — the other ref is simply null.
    const invoiceTableRef = ref(null)
    const itemizedTableRef = ref(null)

    const activeTableRef = () => (activeView.value === 'invoice' ? invoiceTableRef.value : itemizedTableRef.value)

    const exporting = computed(() => {
      const tableRef = activeTableRef()
      return !!(tableRef && tableRef.exporting)
    })

    const handleExport = async () => {
      const tableRef = activeTableRef()
      if (!tableRef) return
      try {
        await tableRef.exportXlsx()
      } catch (e) {
        proxy.$bvToast && proxy.$bvToast.toast(
          'Failed to export. Please try again.',
          { title: 'Error', variant: 'danger', autoHideDelay: 4000 }
        )
      }
    }

    return {
      activeView,
      totalInvoicedAmount,
      totalInvoicedCount,
      handleSummaryChanged,
      formatPlainNumber,

      invoiceTableRef,
      itemizedTableRef,
      exporting,
      handleExport,
    }
  },
})
</script>

<style scoped>
.int-root { padding: 0; }

.int-summary-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}
.int-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 14px 18px;
}
.int-card__label {
  font-size: 10px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.05em; color: #8898aa; margin-bottom: 8px;
}
.int-card__value { font-size: 1.5rem; font-weight: 800; color: #32325d; }
.int-card__value--paid { color: #2dce89; }
.int-card__value--outstanding { color: #f5365c; }
.int-card__sub { font-size: 11px; color: #8898aa; margin-top: 4px; }

.int-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 8px;
}
.int-toolbar__label { font-size: 13px; font-weight: 700; color: #32325d; }
.int-toolbar__actions { display: flex; align-items: center; gap: 10px; }

.int-view-toggle {
  display: flex;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #1c4a50;
}
.int-view-toggle__btn {
  border: none;
  background: #fff;
  color: #1c4a50;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 14px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.int-view-toggle__btn--active {
  background: #1c4a50;
  color: #fff;
}
.int-view-toggle__btn:not(.int-view-toggle__btn--active):hover {
  background: #eef4f4;
}
</style>
